import { z } from 'zod'
// Explicit `.ts` extension keeps this importable from Supabase Edge Runtime (Deno).
import { type Email, normalizeEmail, isValidEmail } from '../email/validation.ts'

/**
 * Zod schema for an email field. Trims, lowercases, then format-checks.
 * Output type is the branded Email — `z.infer<typeof emailSchema>` is Email,
 * not string. Consumers using react-hook-form / tRPC / any Zod-aware tool
 * pick up the brand automatically.
 *
 * Pipeline: string → normalizeEmail (trim + lowercase) → isValidEmail check
 * → cast to branded Email.
 *
 * Use this for FE form schemas and BE payload schemas where the email field
 * is the value being validated. For required-with-distinct-empty-message
 * semantics (i.e. surfacing "Email is required" before "Invalid format"),
 * use requiredEmailSchema below.
 *
 * Typing note: Zod 3's chained .transform().refine().transform() correctly
 * propagates the last transform's output type through ZodEffects layers, so
 * z.infer<typeof emailSchema> resolves to Email (not plain string).
 */
export const emailSchema = z
  .string()
  .transform((raw) => normalizeEmail(raw))
  .refine(isValidEmail, { message: 'Invalid email address' })
  .transform((normalized) => normalized as Email)

/**
 * Same as emailSchema, but distinguishes empty-input from malformed-input
 * with separate error messages — useful in form schemas where the UI shows
 * "Email is required" on first interaction and "Invalid email address"
 * only after content has been entered.
 *
 * Pipeline: trim → non-empty check → pipe to emailSchema. The leading
 * .trim() is essential: without it a whitespace-only input ('   ') passes
 * .min(1) (length 3), then normalizeEmail collapses it to '' and the user
 * sees "Invalid email address" instead of "Email is required". Trimming
 * first means whitespace-only correctly short-circuits with the required
 * message, so users see exactly one error message per state (never both).
 */
export const requiredEmailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .pipe(emailSchema)

/** Inferred output type for emailSchema (branded Email after transforms). */
export type EmailOutput = z.output<typeof emailSchema>
